import { doc, onSnapshot } from 'firebase/firestore';
import { useCallback, useEffect, useState } from 'react';
import { db } from '../../lib/firebase';
import {
  enqueueAction,
  getAction,
  subscribeActionOutboxChanges,
  type ActionOutboxEntry,
} from '../../lib/actionOutbox';

/**
 * 「そう思う」(agree) toggle hook (REQ-4.1.19、Phase 5 PostDetailModal consumer)。
 *
 * 旧 client (PostDetailModal の agree button) を踏襲、v2 hook 化:
 *   - path: `districts/{districtId}/posts/{postId}/agrees/{uid}` (doc ID = uid、存在 = 同感)
 *   - read: 認証済全員、create/delete: 本人のみ (firestore.rules:164-168)
 *   - 集計 (post.agreeCount) は onAgreeWrite trigger が自動更新
 *   - update は禁止 → toggle は delete + create で表現
 *
 * **Phase 2 層1 (docs/03 §7.5-3、2026-06-07)**: 書込を Firestore SDK 直書きから
 * **リース非依存・冪等な action outbox 経由** (`lib/actionOutbox.ts`) に寄せた。
 *   - toggle は server ack を待たず即 enqueue (IndexedDB) するので **オフラインでも hang しない**
 *     (旧: `await setDoc/deleteDoc` は server ack まで resolve せず宙吊りだった)。
 *   - 送信先 doc path を決定的 `clientId` にしているため、連打 (ON→OFF→ON) は outbox 内で
 *     1 件に coalesce され「最終 intent」のみ送られる (agreeCount の二重加算を構造的に防ぐ)。
 *   - 実送信は `useActionOutbox` (AppHome / MapPage に mount) が online 時 / 復帰時に flush。
 *
 * 楽観 update + 未送信 overlay:
 *   - toggle 押下時に server 応答を待たず即座に state.isAgreed を反転 (R-24 踏襲)。
 *   - **未送信 (outbox に pending) の間は outbox の intent を server 値より優先表示**するため、
 *     再 mount / 別タブでも未送信トグルが消えない (旧は local state のみで再 mount で server 値に戻った)。
 *   - flush 成功で outbox entry が消え、onSnapshot の server 確定値に収束する。
 *   - enqueue 自体 (IndexedDB 書込) に失敗した場合のみ元の値に rollback して例外を throw。
 */

export interface AgreeState {
  /** uid 未認証 or 未取得時は null。 */
  isAgreed: boolean | null;
  loading: boolean;
}

/** agree の決定的 clientId (= 送信先 Firestore doc path)。outbox の primary key 兼冪等キー。 */
export function agreeClientId(districtId: string, postId: string, uid: string): string {
  return `districts/${districtId}/posts/${postId}/agrees/${uid}`;
}

export function useAgree(
  districtId: string,
  postId: string,
  uid: string | null,
): {
  state: AgreeState;
  toggle: () => Promise<void>;
} {
  const [state, setState] = useState<AgreeState>({ isAgreed: null, loading: true });

  useEffect(() => {
    if (!uid) {
      setState({ isAgreed: null, loading: false });
      return;
    }
    const ref = doc(db, 'districts', districtId, 'posts', postId, 'agrees', uid);
    const clientId = agreeClientId(districtId, postId, uid);
    let cancelled = false;
    let serverExists: boolean | null = null;
    let gen = 0;

    // server 値 (onSnapshot) と未送信 outbox intent を突き合わせて isAgreed を確定。
    // pending entry があればそれを優先 (未送信トグルの永続表示)。getAction は async のため
    // 世代カウンタで「最新 reconcile のみ反映」(古い await 完了が新しい結果を上書きしない)。
    const reconcile = async () => {
      const myGen = ++gen;
      let pending: ActionOutboxEntry | undefined;
      try {
        pending = await getAction(clientId);
      } catch {
        pending = undefined;
      }
      if (cancelled || myGen !== gen) return;
      const isAgreed = pending ? pending.op === 'set' : serverExists;
      setState({ isAgreed, loading: false });
    };

    const unsub = onSnapshot(
      ref,
      (snap) => {
        serverExists = snap.exists();
        void reconcile();
      },
      () => {
        serverExists = null;
        void reconcile();
      },
    );
    // 他タブ / 同一タブの outbox 変化 (enqueue / flush 完了) で再 reconcile。
    const unsubOutbox = subscribeActionOutboxChanges(() => {
      void reconcile();
    });
    return () => {
      cancelled = true;
      unsub();
      unsubOutbox();
    };
  }, [districtId, postId, uid]);

  const toggle = useCallback(async () => {
    if (!uid) return;
    // isAgreed=null (snapshot エラー後 / 初期 loading 中) は意図せぬ create を防ぐため no-op。
    if (state.isAgreed === null) return;

    const previous = state.isAgreed;
    const next = !previous;

    // 楽観 update: server / flush を待たず即時反転 (R-24)。
    setState((s) => ({ ...s, isAgreed: next }));

    try {
      // リース非依存・冪等な outbox へ enqueue (同一 clientId は coalesce)。
      // 実送信は useActionOutbox の flush が担う (enqueue の notify で online なら即 flush)。
      await enqueueAction({
        clientId: agreeClientId(districtId, postId, uid),
        kind: 'agree',
        op: next ? 'set' : 'delete',
        districtId,
        payload: null,
      });
    } catch (e) {
      // IndexedDB 書込失敗時のみ rollback して呼出側へ伝播 (PostDetailModal で Toast 通知)。
      setState((s) => ({ ...s, isAgreed: previous }));
      throw e;
    }
  }, [districtId, postId, uid, state.isAgreed]);

  return { state, toggle };
}
